import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconButton, } from '@awaze/design-system.components.button';
import { breakpoints, useWindowWidth } from '@awaze/design-system.utils.react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { register } from 'swiper/element/bundle';
import { CameraPhotoPictureImage } from '@awaze/design-system.components.icon';
import { CarouselNavigationElementStyled, CarouselPaginationElementStyled, CarouselStyled, PaginationCountContainer, PaginationCountText, PaginationDivider, } from './carousel.styles';
import { ChevronLeft, ChevronRight } from './icons';
import { CarouselGlobalStyles } from './swiper.styles';
// register Swiper custom elements
register();
/**
 * This component implements open source carousel library [swiperjs](https://swiperjs.com/).
 *
 * Immediate children of the Carousel component will be treated as slides.
 *
 * By default the carousel will loop infinitely. This can be disabled by setting the `loop={false}`.
 */
export const Carousel = ({ children, loop = true, initialSlide = 0, swiperOptions = {}, customPagination = null, hideNavButtons = false, hidePagination = false, navButtonBottomAligned = false, navButtonOverlayed = false, displayOverlayedNavButtons = true, isResponsive = false, navVariant, colourScheme, translations, stretchItems = false, onCarouselInteractCallback, onImageCarouselOverlaySlideChange, }) => {
    const swiperElRef = useRef(null);
    const width = useWindowWidth();
    const [reachedEnd, setReachedEnd] = useState(false);
    const [reachedBeginning, setReachedBeginning] = useState(true);
    const swapNavigationIcons = width > breakpoints[1];
    const isMobile = useMediaQuery({ query: `(max-width: ${breakpoints[0]}px)` });
    const hasPagination = customPagination !== null && typeof customPagination === 'object';
    // Set a unique name from outside the component to control carousels individually.
    const controls = swiperOptions?.controller?.control;
    const childrenCount = React.Children.count(children);
    const [paginationPosition, setPaginationPosition] = useState(1);
    const onNavNext = (event) => {
        const [swiper] = event.detail;
        if (swiper !== undefined) {
            setReachedBeginning(swiper.isBeginning);
            setReachedEnd(swiper.isEnd);
        }
        if (onCarouselInteractCallback) {
            onCarouselInteractCallback('navigate', swiper.realIndex, 'next');
        }
        if (onImageCarouselOverlaySlideChange) {
            onImageCarouselOverlaySlideChange(swiper?.realIndex || 0);
        }
        setPaginationPosition(swiper.realIndex + 1);
    };
    const onNavPrev = (event) => {
        const [swiper] = event.detail;
        if (swiper !== undefined) {
            setReachedBeginning(swiper.isBeginning);
            setReachedEnd(swiper.isEnd);
        }
        if (onCarouselInteractCallback) {
            onCarouselInteractCallback('navigate', swiper.realIndex, 'previous');
        }
        if (onImageCarouselOverlaySlideChange) {
            onImageCarouselOverlaySlideChange(swiper?.realIndex || 0);
        }
        setPaginationPosition(swiper.realIndex + 1);
    };
    const onPaginationUpdate = (event) => {
        const [swiper] = event.detail;
        if (swiper !== undefined) {
            setReachedEnd(swiper.slides?.length / swiper.params?.slidesPerGroup <= 1);
        }
    };
    const onSlideChange = (event) => {
        const [swiper] = event.detail;
        if (swiper.swipeDirection) {
            switch (swiper.swipeDirection) {
                case 'next':
                    onNavNext(event);
                    break;
                case 'prev':
                    onNavPrev(event);
                    break;
            }
        }
        else {
            const isLastToFirst = swiper.realIndex === 0 && swiper.previousRealIndex === childrenCount - 1;
            const isFirstToLast = swiper.realIndex === childrenCount - 1 && swiper.previousRealIndex === 0;
            const isIndexIncreasing = swiper.realIndex > swiper.previousRealIndex;
            const isPaginatingNext = (isIndexIncreasing || isLastToFirst) && !isFirstToLast;
            isPaginatingNext ? onNavNext(event) : onNavPrev(event);
        }
    };
    useEffect(() => {
        if (childrenCount <= 1) {
            setReachedEnd(true);
        }
        if (swiperElRef !== null && swiperElRef.current !== null) {
            swiperElRef.current.addEventListener('swiperpaginationupdate', onPaginationUpdate);
            swiperElRef.current.addEventListener('swiperrealindexchange', onSlideChange);
            return function cleanup() {
                if (swiperElRef !== null && swiperElRef.current !== null) {
                    swiperElRef.current.removeEventListener('swiperpaginationupdate', onPaginationUpdate);
                    swiperElRef.current.removeEventListener('swiperrealindexchange', onSlideChange);
                }
            };
        }
    }, [swiperElRef.current, children]);
    const navVariantMemo = useMemo(() => {
        if (navVariant) {
            return navVariant;
        }
        if (hasPagination) {
            return 'ghost';
        }
        if (swapNavigationIcons) {
            return 'outline';
        }
        return 'solid';
    }, [navVariant]);
    const colourSchemeMemo = useMemo(() => {
        if (colourScheme) {
            return colourScheme;
        }
        if (hasPagination || swapNavigationIcons) {
            return 'brand-secondary';
        }
        return 'brand-tertiary';
    }, [colourScheme]);
    const applyHorizontalPadding = !navButtonOverlayed && !hasPagination && !navButtonBottomAligned;
    return (_jsx(CarouselStyled, { horizontalPadding: applyHorizontalPadding, stretchItems: stretchItems, shouldFillHeight: navVariantMemo === 'darkTransparency', children: _jsx(CarouselGlobalStyles, { shouldFillHeight: navVariantMemo === 'darkTransparency', children: childrenCount > 1 ? (_jsxs(_Fragment, { children: [_jsx("swiper-container", { "set-wrapper-size": true, "space-between": swiperOptions.spaceBetween, ref: swiperElRef, "initial-slide": initialSlide, "navigation-enabled": true, "navigation-next-el": `.${controls || 'swiper'}-next`, "navigation-prev-el": `.${controls || 'swiper'}-prev`, loop: loop, "auto-height": swiperOptions.autoHeight, "slides-per-view": isResponsive
                            ? isMobile
                                ? 1.1
                                : 3
                            : swiperOptions.slidesPerView || 1, "slides-per-group": isResponsive
                            ? isMobile
                                ? 1
                                : 3
                            : swiperOptions.slidesPerGroup || 1, "pagination-dynamic-bullets": hasPagination && customPagination.dynamicBullets == true, pagination: hasPagination, "pagination-el": hasPagination ? `.${controls || 'swiper'}-pagination` : '', "pagination-type": hasPagination ? customPagination.type : '', virtual: childrenCount > 3 && swiperOptions.virtual === true, 
                        // HACK: needed so carousel can grow to the height of the parent
                        style: {
                            ...(navVariantMemo === 'darkTransparency' && {
                                height: '100%',
                            }),
                        }, children: React.Children.map(children, (child) => (_jsx("swiper-slide", { children: child }))) }), navVariantMemo === 'darkTransparency' && (_jsxs(PaginationCountContainer, { children: [_jsx(CameraPhotoPictureImage, { size: "s", iconColour: "ColorWhiteOpacity100" }), _jsx(PaginationCountText, { children: `${paginationPosition}/${childrenCount}` })] })), _jsxs(CarouselPaginationElementStyled, { hasPagination: hasPagination || navButtonBottomAligned, children: [_jsx(CarouselNavigationElementStyled, { type: "prev", defaultNavigationButtonPosition: navButtonBottomAligned ? false : !hasPagination, className: `swiper-prev ${controls}-prev`, hidden: hideNavButtons, applyResponsiveStyling: navButtonOverlayed, children: displayOverlayedNavButtons && (_jsx(IconButton, { className: hasPagination && 'swiper-pagination-nav-icon-color', colourScheme: colourSchemeMemo, icon: _jsx(ChevronLeft, {}), isDisabled: !loop && reachedBeginning, size: hasPagination
                                        ? 'small'
                                        : swapNavigationIcons
                                            ? 'medium'
                                            : 'small', title: translations.previousNavigation, variant: navVariantMemo })) }), hidePagination || navButtonBottomAligned ? (_jsx(PaginationDivider, {})) : (_jsx("div", { className: `swiper-pagination ${controls}-pagination`, hidden: !hasPagination })), _jsx(CarouselNavigationElementStyled, { type: "next", defaultNavigationButtonPosition: navButtonBottomAligned ? false : !hasPagination, className: `swiper-next ${controls}-next`, hidden: hideNavButtons, applyResponsiveStyling: navButtonOverlayed, children: displayOverlayedNavButtons && (_jsx(IconButton, { className: hasPagination && 'swiper-pagination-nav-icon-color', colourScheme: colourSchemeMemo, icon: _jsx(ChevronRight, {}), isDisabled: !loop && reachedEnd, size: hasPagination || !swapNavigationIcons ? 'small' : 'medium', title: translations.nextNavigation, variant: navVariantMemo })) })] })] })) : (children) }) }));
};
//# sourceMappingURL=carousel.js.map